import { Alert, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import React from "react";
import { MyPaper, SectionPaper } from "../../../components";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useLazyGetAttachmentUrlQuery, useUpdateQuoteMutation } from "../../../redux/services";

// Import styles
import { Download, Settings } from "@mui/icons-material";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/highlight/lib/styles/index.css";
import { openDialog } from "../../../redux/slices/dialog";
import { QuoteReportConfig } from "./config";
import { useSnackbar } from "notistack";
import { buildObjectForState, preparePayload } from "../utils";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { matchPath } from "react-router-dom";
import { AppRoutes } from "../../../router/routes";
import { updateQuoteSlice } from "../../../redux/slices/quote";

export const QuoteReport: React.FC<{ quoteId: string, refetch: any, readOnly?: boolean, loading?: boolean }> = ({ quoteId, readOnly, loading, refetch }) => {

    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    const dispatch = useAppDispatch();
    const { quote } = useAppSelector(store => store);
    const { enqueueSnackbar } = useSnackbar();

    const [getAttachmentURL] = useLazyGetAttachmentUrlQuery();
    const [updateQuote] = useUpdateQuoteMutation();

    const [url, setURL] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const interval = React.useRef<NodeJS.Timeout | null>(null);

    const title = quote.name;

    const failedAttachment = quote.attachments?.find(a => a._id === quote.quote_report?.attachment_id)?.status === "failed";

    const generateQuoteReportAPICall = async () => {

        if (!quote.quote_report) {
            let payload = { ...preparePayload({ ...quote }) };

            if (!quote.released) {
                dispatch(openBackdrop('Saving Quote Report Configuration...'));
                await updateQuote({ id: quoteId, payload });
                enqueueSnackbar("Quote report configuration saved successfully!", { variant: "success" });
                dispatch(closeBackdrop());
            };
        }
    };

    const checkReportStatus = async () => {
        // Avoid setting up multiple intervals
        if (interval.current) return;

        if (failedAttachment) {
            clearInterval(interval.current ?? undefined); // Clear the interval
            interval.current = null;
            setIsLoading(false);
            return;
        }

        // Set up a polling mechanism with setInterval
        interval.current = setInterval(async () => {

            try {
                // Checking wheather the page is navigated
                if (!matchPath(window.location.pathname, AppRoutes.viewQuickQuoteReportGeneration(quoteId))) {
                    clearInterval(interval.current ?? undefined); // Clear the interval
                    interval.current = null;
                }

                // Fetch the URL using getAttachmentURL
                const response = await refetch(quoteId);

                // If the URL is fetched successfully
                if (response.data?.quote_report?.attachment_id) {
                    let newState = buildObjectForState(response);
                    dispatch(updateQuoteSlice(newState));

                    const newURL = await getAttachmentURL({
                        id: response.data.quote_report?.attachment_id,
                        object_type: "quote",
                        object_id: quoteId,
                    }).unwrap();

                    setURL(newURL);

                    if (newURL) {
                        clearInterval(interval.current ?? undefined); // Clear the interval
                        interval.current = null;
                        setIsLoading(false);
                    }
                }

                if (response.data?.released) {
                    clearInterval(interval.current ?? undefined); // Clear the interval
                    interval.current = null;
                }

            } catch (error) {
                console.error("Error fetching report status:", error);
                // Handle errors (optional: retry logic or notify the user)
            }
        }, 5 * 1000);
    };

    const handleDownload = () => {
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = `${title}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const openConfig = () => {
        dispatch(openDialog({
            title: "Quote Report Configuration",
            body: <QuoteReportConfig quoteId={quoteId} onSaved={generateQuoteReportAPICall} />,
            maxWidth: "md",
            hideNegativeBtn: true,
            hidePositiveBtn: true
        }));
    };

    React.useEffect(() => {
        if (quote.quote_report?.processing === false) {
            const fetchAttachmentUrl = async () => {
                setIsLoading(true);
                try {
                    // Fetch the URL using getAttachmentURL
                    const response = await refetch(quoteId);

                    // If the URL is fetched successfully
                    if (response.data?.quote_report?.attachment_id) {
                        let newState = buildObjectForState(response);
                        dispatch(updateQuoteSlice(newState));

                        const newURL = await getAttachmentURL({
                            id: response.data.quote_report?.attachment_id,
                            object_type: "quote",
                            object_id: quoteId,
                        }).unwrap();

                        if (newURL) {
                            setURL(newURL);
                        } else {
                            checkReportStatus();
                        }
                    }
                } catch (error) {
                    console.error("Error fetching attachment URL:", error);
                }
            };

            fetchAttachmentUrl();
        } else if ((quote.quote_report?.attachment_id && quote.quote_report?.processing === true) || !quote.quote_report?.attachment_id) {
            checkReportStatus();
        }
        // eslint-disable-next-line
    }, [quote.quote_report?.processing, quote.quote_report?.attachment_id, failedAttachment]);

    React.useEffect(() => {
        if (url) {
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }
        // eslint-disable-next-line
    }, [url]);

    return (
        <Stack spacing={1} sx={{ height: "calc(100% - 10px)" }}>

            {/* Header */}
            <SectionPaper
                title={"Quote Report"}
                isExpand={false}
                hideExpandCollapse={true}
                renderButtons={[
                    <Stack direction="row" spacing={1}>
                        {false && <Button size="small" variant="outlined" disabled={readOnly || quote.released} startIcon={<Settings />} onClick={openConfig}>Config</Button>}
                        <Button size="small" variant="contained" disabled={!url} startIcon={<Download />} onClick={handleDownload}>Download</Button>
                    </Stack>
                ]}
            />

            {/* Error Message */}
            {(quote.quote_report?.attachment_id && quote.quote_report?.processing === false && !isLoading && !loading && failedAttachment) && <MyPaper>
                <Alert severity="error">
                    We're unable to generate the quote report at the moment. Please try to update the quote and try again later or contact support if the issue persists.
                </Alert>
            </MyPaper>
            }

            {/* PDF Viewer */}
            {(quote.quote_report?.attachment_id && quote.quote_report?.processing === false && !isLoading && !loading) && url && <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                <div style={{ width: "100%", height: "calc(100% - 69px)" }}>
                    <Viewer fileUrl={url} plugins={[defaultLayoutPluginInstance]} />
                    
                    <Typography variant='caption' color='textSecondary'>
                        Report generated in timezone: {quote.time_zone}
                    </Typography>
                </div>
            </Worker>}

            {/* Loading Message */}
            {(quote.quote_report?.processing || isLoading || loading) && !failedAttachment && <MyPaper>
                <Alert severity="info" iconMapping={{ info: <CircularProgress size={"1em"} color="info" /> }}>
                    The quote report is being generated. Please wait for a moment.
                </Alert>
            </MyPaper>}

            {/* Error Message */}
            {quote.released && !quote.quote_report?.name && <MyPaper>
                <Alert severity="info">
                    This quote is released before generating the quote report. Please clone this quote and generate the quote report.
                </Alert>
            </MyPaper>}
        </Stack>
    );
};